import { c as _c } from "react/compiler-runtime";
import { SYSTEM_GENERATED_CONNECTORS_BQ_PROJECT_ID } from 'src/connectors/constants';
import { useMutation, type UseMutationResult, useQuery, type UseQueryResult, useQueryClient, type QueryClient, useQueries } from '@tanstack/react-query';
import * as connectors from 'shared/api/connectors';
import type * as fetch from 'shared/api/fetch';
import * as clients from 'shared/api/clients';
import * as accounts from 'shared/api/accounts';
import * as analytics from 'shared/api/analytics';
import { type ConnectorResp, type ConnectorsResp } from 'shared/api/connectors';
export const queryKeys = {
  clientConnectors: 'clientConnectors',
  configStatus: 'configStatus',
  reportRegistry: 'reportRegistry',
  exploreConnectorsData: 'exploreConnectorsData',
  userDetails: 'userDetails',
  userDetailsByEmails: 'userDetailsByEmails',
  spreadsheetTabs: 'spreadsheetTabs',
  connectorConfigDagStatus: 'connectorConfigDagStatus'
};
const updateConfigSystemGenerated: (config: ConnectorResp) => ConnectorResp = config => {
  config.systemGenerated ||= config.targetProjectId === SYSTEM_GENERATED_CONNECTORS_BQ_PROJECT_ID || (config.destinationTable ?? '').split('.')[0] === SYSTEM_GENERATED_CONNECTORS_BQ_PROJECT_ID;
  return config;
};
export const useQueryConnectorConfigs = (clientId, t0) => {
  const $ = _c(7);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  let t2;
  if ($[0] !== clientId) {
    t1 = [queryKeys.clientConnectors, clientId];
    t2 = async () => {
      const configsResponse = await connectors.getAllConnectorsForClient(clientId);
      return configsResponse.map(updateConfigSystemGenerated);
    };
    $[0] = clientId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  const t3 = Boolean(clientId) && enabled;
  let t4;
  if ($[3] !== t1 || $[4] !== t2 || $[5] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return useQuery(t4);
};
const getUseCache = (queryClient: QueryClient, clientId: number): boolean => {
  const queriesData = queryClient.getQueriesData({
    queryKey: [queryKeys.configStatus, clientId]
  });
  const useCache = queriesData.every(queryData => queryData?.[1] == null);
  return useCache;
};
export const useQueryConnectorConfigStatuses = clientId => {
  const $ = _c(2);
  let t0;
  if ($[0] !== clientId) {
    t0 = {
      queryKey: [queryKeys.configStatus, clientId],
      queryFn: async t1 => {
        const {
          client
        } = t1;
        const useCache = getUseCache(client, clientId);
        return await analytics.postConfigStatuses("connectors_client_configs_status_ui", clientId, useCache);
      }
    };
    $[0] = clientId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useQuery(t0);
};
export const useQueryConnectorConfigStatusById = (clientId, configId, t0) => {
  const $ = _c(8);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  let t2;
  if ($[0] !== clientId || $[1] !== configId) {
    t1 = [queryKeys.configStatus, clientId, configId];
    t2 = async t3 => {
      const {
        client
      } = t3;
      const useCache = getUseCache(client, clientId);
      return await analytics.postConfigStatusById("connectors_config_status_ui", clientId, configId, useCache);
    };
    $[0] = clientId;
    $[1] = configId;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  let t3;
  if ($[4] !== enabled || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2,
      enabled
    };
    $[4] = enabled;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
};
export const useQueryConnectorConfigDagStatusById = (clientId, configId, t0, t1) => {
  const $ = _c(9);
  const enabled = t0 === undefined ? true : t0;
  const useCache = t1 === undefined ? true : t1;
  let t2;
  let t3;
  if ($[0] !== clientId || $[1] !== configId || $[2] !== useCache) {
    t2 = [queryKeys.connectorConfigDagStatus, clientId, configId, useCache];
    t3 = async () => await analytics.postConfigStatusById("connectors_dag", clientId, configId, useCache);
    $[0] = clientId;
    $[1] = configId;
    $[2] = useCache;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  const t4 = Boolean(clientId) && enabled;
  let t5;
  if ($[5] !== t2 || $[6] !== t3 || $[7] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4
    };
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return useQuery(t5);
};
export const useQueryReportRegistry = clientId => {
  const $ = _c(7);
  let t0;
  let t1;
  if ($[0] !== clientId) {
    t0 = [queryKeys.reportRegistry, clientId];
    t1 = async () => await connectors.getReportRegistryByClient(clientId);
    $[0] = clientId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  const t2 = Boolean(clientId);
  let t3;
  if ($[3] !== t0 || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return useQuery(t3);
};
export const useMutationCreateConnector = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: connectors.postConnector,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.clientConnectors]
        });
        queryClient.invalidateQueries({
          queryKey: [queryKeys.configStatus]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useQueryConnectorConfigById = (clientId, configId, t0) => {
  const $ = _c(8);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  let t2;
  if ($[0] !== clientId || $[1] !== configId) {
    t1 = [queryKeys.clientConnectors, clientId, configId];
    t2 = async () => {
      const configResponse = await connectors.getConnectorForClient(clientId, configId);
      return updateConfigSystemGenerated(configResponse);
    };
    $[0] = clientId;
    $[1] = configId;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  const t3 = Boolean(clientId) && enabled;
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return useQuery(t4);
};
export const useMutationUpdateConnector = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: connectors.updateConnector,
      onMutate: async updatedConfigData => {
        await queryClient.cancelQueries({
          queryKey: [queryKeys.clientConnectors, updatedConfigData.clientId, updatedConfigData.configId]
        });
        const previousConfig = queryClient.getQueryData([queryKeys.clientConnectors, updatedConfigData.clientId, updatedConfigData.configId]);
        previousConfig != null && queryClient.setQueryData([queryKeys.clientConnectors, updatedConfigData.clientId, updatedConfigData.configId], {
          ...previousConfig,
          ...updatedConfigData
        });
        return {
          previousConfig,
          updatedConfigData
        };
      },
      onError: (_, __, context) => {
        context?.previousConfig != null && queryClient.setQueryData([queryKeys.clientConnectors, context?.updatedConfigData.clientId, context?.updatedConfigData.configId], context.previousConfig);
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.clientConnectors]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useMutationUpdateConnectors = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp,
      onMutate: async t1 => {
        const {
          clientId: clientId_0,
          updates: updates_0
        } = t1;
        await queryClient.cancelQueries({
          queryKey: [queryKeys.clientConnectors, clientId_0]
        });
        const previousConfigs = updates_0.map(updateData_0 => {
          const queryKey = [queryKeys.clientConnectors, clientId_0, updateData_0.configId];
          const previousConfig = queryClient.getQueryData(queryKey);
          if (previousConfig != null) {
            queryClient.setQueryData(queryKey, {
              ...previousConfig,
              ...updateData_0
            });
          }
          return {
            configId: updateData_0.configId,
            previousConfig
          };
        });
        return {
          previousConfigs,
          clientId: clientId_0
        };
      },
      onError: (_, t2, context) => {
        const {
          clientId: clientId_1
        } = t2;
        if (context?.previousConfigs != null) {
          context.previousConfigs.forEach(t3 => {
            const {
              configId,
              previousConfig: previousConfig_0
            } = t3;
            if (previousConfig_0 != null) {
              queryClient.setQueryData([queryKeys.clientConnectors, clientId_1, configId], previousConfig_0);
            }
          });
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.clientConnectors]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useMutationDeleteConnector = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: connectors.deleteConnector,
      onMutate: async deletedConfig => {
        await queryClient.cancelQueries({
          queryKey: [queryKeys.clientConnectors, deletedConfig.clientId]
        });
        const existingConfigs = queryClient.getQueryData([queryKeys.clientConnectors, deletedConfig.clientId]);
        queryClient.setQueryData([queryKeys.clientConnectors, deletedConfig.clientId], oldConfigs => oldConfigs == null ? [] : oldConfigs.filter(config => config.configId !== deletedConfig.configId));
        return {
          existingConfigs
        };
      },
      onError: (_, __, context) => {
        queryClient.setQueryData([queryKeys.clientConnectors], context?.existingConfigs);
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.clientConnectors]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useMutationDeleteConnectors = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp2,
      onMutate: async t1 => {
        const {
          clientId: clientId_0,
          configIds: configIds_0
        } = t1;
        await queryClient.cancelQueries({
          queryKey: [queryKeys.clientConnectors, clientId_0]
        });
        const existingConfigs = queryClient.getQueryData([queryKeys.clientConnectors, clientId_0]);
        queryClient.setQueryData([queryKeys.clientConnectors, clientId_0], oldConfigs => oldConfigs == null ? [] : oldConfigs.filter(config => !configIds_0.includes(config.configId)));
        return {
          existingConfigs
        };
      },
      onError: (_, t2, context) => {
        const {
          clientId: clientId_1
        } = t2;
        if (context?.existingConfigs != null) {
          queryClient.setQueryData([queryKeys.clientConnectors, clientId_1], context.existingConfigs);
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.clientConnectors]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useMutationRunConnectorConfigNow = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: connectors.runConnector,
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.configStatus]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useMutationRunConnectorConfigsNow = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp3,
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.configStatus]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useQueryExploreConnectorsTable = (clientId, configId) => {
  const $ = _c(3);
  let t0;
  if ($[0] !== clientId || $[1] !== configId) {
    t0 = {
      queryKey: [queryKeys.exploreConnectorsData, clientId, configId],
      queryFn: async () => await connectors.exploreConnectorsTable({
        configId,
        clientId
      }),
      throwOnError: false
    };
    $[0] = clientId;
    $[1] = configId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return useQuery(t0);
};
export const useQueriesUserProfilesFromConnectorConfigs = connectorConfigs => {
  const $ = _c(4);
  let t0;
  if ($[0] !== connectorConfigs) {
    t0 = connectorConfigs === undefined ? [] : connectorConfigs.filter(_temp4).map(_temp5);
    $[0] = connectorConfigs;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = {
      queries: t0
    };
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return useQueries(t1);
};
export const useQueryUserProfileFromConnectorConfig = connectorConfig => {
  const $ = _c(10);
  const t0 = connectorConfig?.updatedBy ?? "";
  const t1 = connectorConfig?.clientId;
  let t2;
  if ($[0] !== t0 || $[1] !== t1) {
    t2 = [queryKeys.userDetails, t0, t1];
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== connectorConfig?.clientId || $[4] !== connectorConfig?.updatedBy) {
    t3 = async () => await clients.getUserDetailsByEmail(connectorConfig?.updatedBy ?? "", connectorConfig?.clientId ?? 0);
    $[3] = connectorConfig?.clientId;
    $[4] = connectorConfig?.updatedBy;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = connectorConfig !== undefined;
  let t5;
  if ($[6] !== t2 || $[7] !== t3 || $[8] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4
    };
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return useQuery(t5);
};
export const useQuerySpreadsheetMetadata = (clientId, accountId) => {
  const $ = _c(8);
  let t0;
  let t1;
  if ($[0] !== accountId || $[1] !== clientId) {
    t0 = [queryKeys.spreadsheetTabs, clientId, accountId];
    t1 = async () => await accounts.getGoogleSheetsAccountMetadata(clientId, accountId ?? 0);
    $[0] = accountId;
    $[1] = clientId;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  const t2 = accountId != null;
  let t3;
  if ($[4] !== t0 || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
};
export const useQueryGcpMetadata = (clientId, accountId) => {
  const $ = _c(8);
  let t0;
  let t1;
  if ($[0] !== accountId || $[1] !== clientId) {
    t0 = [clientId, accountId];
    t1 = async () => await accounts.getGCPAccountMetadata(clientId, accountId ?? 0);
    $[0] = accountId;
    $[1] = clientId;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  const t2 = accountId != null;
  let t3;
  if ($[4] !== t0 || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      staleTime: Infinity,
      gcTime: Infinity
    };
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
};
async function _temp(t0) {
  const {
    clientId,
    updates
  } = t0;
  const updatePromises = updates.map(async updateData => {
    ;
    try {
      const result = await connectors.updateConnector({
        ...updateData,
        clientId
      });
      return {
        configId: updateData.configId,
        success: true,
        data: result
      };
    } catch (t1) {
      const error = t1;
      return {
        configId: updateData.configId,
        success: false,
        error: error as fetch.ClientError
      };
    }
  });
  const results = await Promise.allSettled(updatePromises);
  return results.map((result_0, index) => {
    if (result_0.status === "fulfilled") {
      return result_0.value;
    } else {
      return {
        configId: updates[index].configId,
        success: false,
        error: result_0.reason as fetch.ClientError
      };
    }
  });
}
async function _temp2(t0) {
  const {
    clientId,
    configIds
  } = t0;
  const deletePromises = configIds.map(async configId => {
    ;
    try {
      await connectors.deleteConnector({
        clientId,
        configId
      });
      return {
        configId,
        success: true
      };
    } catch (t1) {
      const error = t1;
      return {
        configId,
        success: false,
        error: error as fetch.ClientError
      };
    }
  });
  const results = await Promise.allSettled(deletePromises);
  return results.map((result, index) => {
    if (result.status === "fulfilled") {
      return result.value;
    } else {
      return {
        configId: configIds[index],
        success: false,
        error: result.reason as fetch.ClientError
      };
    }
  });
}
async function _temp3(t0) {
  const {
    clientId,
    configIds
  } = t0;
  const runPromises = configIds.map(async configId => {
    ;
    try {
      await connectors.runConnector({
        clientId,
        configId
      });
      return {
        configId,
        success: true
      };
    } catch (t1) {
      const error = t1;
      return {
        configId,
        success: false,
        error: error as fetch.ClientError
      };
    }
  });
  const results = await Promise.allSettled(runPromises);
  return results.map((result, index) => {
    if (result.status === "fulfilled") {
      return result.value;
    } else {
      return {
        configId: configIds[index],
        success: false,
        error: result.reason as fetch.ClientError
      };
    }
  });
}
function _temp4(config) {
  return config.updatedBy != null;
}
function _temp5(config_0) {
  return {
    queryKey: [queryKeys.userDetailsByEmails, config_0.updatedBy],
    queryFn: async () => await clients.getUserDetailsByEmail(config_0.updatedBy, config_0.clientId)
  };
}